import { CSSProperties, FC, useCallback } from 'react';

import { styled } from '../../theme';
import { Size } from '../../utility/common-props-helpers';
import Tooltip from '../Tooltip';
import { ToggleOption } from './types';

export const OptionButtonRoot = styled('button', {
    shouldForwardProp: (prop) => !['isSelected', 'isDisabled', 'size'].includes(prop),
})<{
    isSelected: boolean;
    isDisabled?: boolean;
    size?: Size;
}>(({ theme, isSelected, isDisabled, size }) => ({
    position: 'relative',
    zIndex: 1,
    flex: '1 1 0',
    border: 'none',
    fontFamily: theme.typography.fontFamily,
    whiteSpace: 'nowrap',
    textAlign: 'center',
    cursor: isSelected || isDisabled ? 'default' : 'pointer',
    pointerEvents: isSelected || isDisabled ? 'none' : 'auto',
    transition: 'color 220ms ease',
    minWidth: size === 'xs' ? 100 : 0,
    background: 'transparent',
    fontSize: 16,
    fontWeight: isSelected ? theme.typography.fontWeight.medium : theme.typography.fontWeight.roman,
    lineHeight: '24px',
    padding: size === 'xs' ? '0 8px' : '0 16px',
    color: isDisabled
        ? theme.color.disabled
        : isSelected
          ? theme.color.white
          : theme.color.jetBlack,
    ':hover': {
        color: isSelected ? theme.color.white : theme.color.primary1,
    },
    '@media (prefers-reduced-motion: reduce)': {
        transition: 'none',
    },
}));

const TRIGGER_WRAPPER_STYLES: CSSProperties = {
    display: 'flex',
    flex: '1 1 0',
    minWidth: 0,
};

type Props = {
    option: ToggleOption;
    isSelected: boolean;
    isDisabled?: boolean;
    buttonStyle?: CSSProperties;
    size?: Size;
    tooltipOffset?: number;
    onSelect: (value: string) => void;
};

const OptionButton: FC<Props> = ({
    option,
    isSelected,
    isDisabled,
    buttonStyle,
    size,
    tooltipOffset,
    onSelect,
}) => {
    const handleClick = useCallback(() => onSelect(option.value), [onSelect, option.value]);

    const button = (
        <OptionButtonRoot
            type="button"
            isSelected={isSelected}
            isDisabled={isDisabled}
            size={size}
            aria-pressed={isSelected}
            aria-disabled={isDisabled}
            onClick={handleClick}
            style={buttonStyle}
        >
            {option.label}
        </OptionButtonRoot>
    );

    if (option.tooltipTitle) {
        return (
            <Tooltip
                placement="top"
                title={option.tooltipTitle}
                offset={tooltipOffset}
                wrapperStyles={TRIGGER_WRAPPER_STYLES}
            >
                {button}
            </Tooltip>
        );
    }

    return button;
};

export default OptionButton;
